import React, { useState, useEffect } from 'react';
import { memberService, bookingService } from '../services/api';
import { socketService } from '../services/socketService';
import Avatar from './Avatar';
import './AdminMembers.css';

export default function AdminMembers({ user, onUpdate, allBookings, assets }) {
  const [members, setMembers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateForm, setUpdateForm] = useState({ userId: '', field: 'name', value: '' });
  const [updateMessage, setUpdateMessage] = useState('');
  const [removeMessage, setRemoveMessage] = useState('');
  const [removeMessageType, setRemoveMessageType] = useState('error'); // 'success' or 'error'
  const [pendingMessage, setPendingMessage] = useState('');

  // User details modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  // Modal edit state (must be top-level for hooks)
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editMessage, setEditMessage] = useState('');
  const [modalRemoveMessage, setModalRemoveMessage] = useState('');
  const [modalRemoveMessageType, setModalRemoveMessageType] = useState('error'); // 'success' or 'error'
  const [fullBookings, setFullBookings] = useState(null);
  const [bookingHistoryLoading, setBookingHistoryLoading] = useState(false);
  const [bookingHistoryLoaded, setBookingHistoryLoaded] = useState(false);
  const [bookingHistoryError, setBookingHistoryError] = useState('');

  // Helper to start editing a field in the modal
  const startEdit = (field) => {
    if (!selectedUser) return;
    setEditField(field);
    if (field === 'avatar') setEditValue('');
    else setEditValue(selectedUser[field] || '');
    setEditMessage('');
  };

  // Handle save edit in the modal
  const saveEdit = async (e) => {
    e.preventDefault();
    setEditMessage('');
    try {
      const valueToSend = editField === 'avatar' ? '' : editValue.trim();
      await memberService.update(
        'update',
        selectedUser._id,
        editField,
        valueToSend
      );
      // Update selectedUser state immediately for instant UI feedback
      setSelectedUser(prev => {
        if (!prev) return prev;
        if (editField === 'avatar') {
          // Remove avatar property or set to null/empty
          const updated = { ...prev };
          delete updated.avatar;
          return updated;
        }
        return { ...prev, [editField]: valueToSend };
      });
      setEditMessage(editField === 'avatar' ? 'Avatar removed successfully' : 'Member updated successfully');
      setEditField('');
      setEditValue('');
      fetchMembers();
    } catch (err) {
      setEditMessage(err.response?.data?.message || err.message || 'Failed to update member');
    }
  };

  // Handle remove user in the modal
  const removeUser = async () => {
    setModalRemoveMessage('');
    setModalRemoveMessageType('error');
    try {
      await memberService.update('remove', selectedUser._id);
      setModalRemoveMessage('User removed successfully');
      setModalRemoveMessageType('success');
      setTimeout(() => {
        setShowUserDetails(false);
        fetchMembers();
      }, 1000);
    } catch (err) {
      setModalRemoveMessage(err.response?.data?.message || err.message || 'Failed to remove user');
      setModalRemoveMessageType('error');
    }
  };

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const data = await memberService.list();
      setMembers(data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('You do not have permission to view this webpage. Admin access required.');
      } else {
        setError(err.message || 'Failed to load members');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      setPendingLoading(true);
      const data = await memberService.listPending();
      setPendingUsers(data);
    } catch (err) {
      console.error('Failed to load pending users:', err);
      // Don't set error for pending users as it's not critical
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchPendingUsers();
    
    // Listen for real-time user profile updates
    const unsubscribeProfileUpdate = socketService.onUserProfileUpdated((data) => {
      console.log('Received user profile update:', data);
      
      // Update the members list in real-time
      setMembers(prevMembers => 
        prevMembers.map(member => 
          member._id === data.user.id 
            ? { ...member, ...data.user }
            : member
        )
      );
      
      // If this is the current admin user, update their profile too
      if (data.user.id === user.id && onUpdate) {
        onUpdate(data.user);
      }
    });
    
    // Cleanup on unmount
    return () => {
      unsubscribeProfileUpdate();
    };
  }, [user.id, onUpdate]);

  const handleApprovePending = async (userId) => {
    setPendingMessage('');
    try {
      await memberService.approvePending(userId);
      setPendingMessage('User approved and magic link sent');
      fetchPendingUsers();
      fetchMembers(); // Refresh members list as well
    } catch (err) {
      setPendingMessage(err.response?.data?.message || err.message || 'Failed to approve user');
    }
  };

  const handleRejectPending = async (userId) => {
    setPendingMessage('');
    try {
      await memberService.rejectPending(userId);
      setPendingMessage('User rejected and removed');
      fetchPendingUsers();
    } catch (err) {
      setPendingMessage(err.response?.data?.message || err.message || 'Failed to reject user');
    }
  };

  const handleRemove = async (userId) => {
    setRemoveMessage('');
    setRemoveMessageType('error');
    try {
      await memberService.update('remove', userId);
      // Refresh list after removal
      fetchMembers();
    } catch (err) {
      console.error('Remove error', err);
      setRemoveMessage(err.response?.data?.message || err.message || 'Failed to remove user');
      setRemoveMessageType('error');
    }
  };

  const handleUpdateInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateForm(prev => {
      if (name === 'field') {
        // Find the member being edited
        const member = members.find(m => m._id === prev.userId);
        let newValue = '';
        if (member) {
          if (value === 'name') newValue = member.name;
          else if (value === 'email') newValue = member.email;
          else if (value === 'role') newValue = member.role || '';
          else if (value === 'sailingNumber') newValue = String(member.sailingNumber);
          else if (value === 'avatar') newValue = ''; // Empty string to remove avatar
        }
        return { ...prev, field: value, value: newValue };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdateMessage('');
    try {
      const valueToSend = updateForm.field === 'avatar' ? '' : updateForm.value.trim();
      await memberService.update(
        'update',
        updateForm.userId,
        updateForm.field,
        valueToSend
      );
      
      // Check if the admin updated their own information
      if (updateForm.userId === user.id && updateForm.field === 'name' && onUpdate) {
        // Update the current user state so welcome text and socket events work correctly
        const updatedUser = { ...user, name: updateForm.value.trim() };
        onUpdate(updatedUser);
      }
      
      const successMessage = updateForm.field === 'avatar' ? 'Avatar removed successfully' : 'Member updated successfully';
      setUpdateMessage(successMessage);
      setUpdateForm({ userId: '', field: 'name', value: '' });
      fetchMembers();
    } catch (err) {
      setUpdateMessage(err.response?.data?.message || err.message || 'Failed to update member');
    }
  };

  // Calculate comprehensive user statistics
  const fetchFullBookingHistory = async () => {
    if (bookingHistoryLoading || bookingHistoryLoaded) {
      return;
    }

    setBookingHistoryLoading(true);
    setBookingHistoryError('');

    try {
      const response = await bookingService.getAll(null, true, { scope: 'all' });
      setFullBookings(response.data || {});
      setBookingHistoryLoaded(true);
    } catch (err) {
      console.error('Failed to load booking history for admin members:', err);
      setBookingHistoryError(err.message || 'Failed to load booking history.');
    } finally {
      setBookingHistoryLoading(false);
    }
  };

  const calculateUserStats = (userId, bookingsSource = allBookings) => {
    if (!bookingsSource) {
      return {
        totalBookings: 0,
        pastBookings: [],
        futureBookings: [],
        assetUsage: [],
        totalHours: 0
      };
    }

    const allUserBookings = [];

    // Create a lookup map for asset names
    const assetNameMap = {};
    if (assets && Array.isArray(assets)) {
      assets.forEach(asset => {
        assetNameMap[asset._id] = asset.name;
      });
    }

    // Collect all bookings for this user across all assets
    Object.entries(bookingsSource).forEach(([assetId, bookings]) => {
      // Get the actual asset name from the assets array
      const actualAssetName = assetNameMap[assetId] || `Asset ${assetId}`;
      
      bookings.forEach(booking => {
        if (booking.userId === userId || booking.name === selectedUser?.name || booking.email === selectedUser?.email) {
          allUserBookings.push({
            ...booking,
            assetId,
            assetName: actualAssetName
          });
        }
      });
    });

    const now = new Date();
    const pastBookings = allUserBookings.filter(b => new Date(b.end) < now);
    const futureBookings = allUserBookings.filter(b => new Date(b.start) > now);

    // Track usage by asset - ONLY for past bookings
    const assetUsageMap = {};
    pastBookings.forEach(booking => {
      const assetId = booking.assetId;
      const actualAssetName = assetNameMap[assetId] || `Asset ${assetId}`;
      
      if (!assetUsageMap[assetId]) {
        assetUsageMap[assetId] = {
          assetName: actualAssetName,
          bookingCount: 0,
          totalHours: 0
        };
      }
      assetUsageMap[assetId].bookingCount++;
      const duration = (new Date(booking.end) - new Date(booking.start)) / (1000 * 60 * 60);
      assetUsageMap[assetId].totalHours += duration;
    });

    // Convert asset usage map to sorted array
    const assetUsage = Object.entries(assetUsageMap)
      .map(([assetId, data]) => ({
        assetId,
        assetName: data.assetName,
        bookingCount: data.bookingCount,
        totalHours: Math.round(data.totalHours * 10) / 10
      }))
      .sort((a, b) => b.totalHours - a.totalHours);

    const totalHours = assetUsage.reduce((sum, asset) => sum + asset.totalHours, 0);

    return {
      totalBookings: allUserBookings.length,
      pastBookings: pastBookings.sort((a, b) => new Date(b.start) - new Date(a.start)),
      futureBookings: futureBookings.sort((a, b) => new Date(a.start) - new Date(b.start)),
      assetUsage,
      totalHours: Math.round(totalHours * 10) / 10
    };
  };

  // Handle user row click to show details
  const handleUserClick = (member) => {
    setSelectedUser(member);
    setShowUserDetails(true);
    fetchFullBookingHistory();
  };

  // Close user details modal
  const closeUserDetails = () => {
    setShowUserDetails(false);
    setSelectedUser(null);
  };

  if (loading) return <div>Loading approved members...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div className="admin-members-container">
      <h2>Pending Access Requests</h2>
      {pendingMessage && <div style={{ color: pendingMessage.includes('approved') || pendingMessage.includes('rejected') ? 'green' : 'red', marginBottom: 8 }}>{pendingMessage}</div>}
      
      {pendingLoading ? (
        <div>Loading pending requests...</div>
      ) : pendingUsers.length === 0 ? (
        <div style={{ color: 'gray', marginBottom: 20 }}>No pending access requests</div>
      ) : (
        <table className="admin-members-table" style={{ marginBottom: 30 }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Sailing Number</th>
              <th>Requested</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingUsers.map(user => (
              <tr key={user._id}>
                <td>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.sailingNumber}</td>
                <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : new Date(parseInt(user._id.substring(0, 8), 16) * 1000).toLocaleDateString()}</td>
                <td>
                  <button 
                    onClick={() => handleApprovePending(user._id)}
                    style={{ backgroundColor: '#27ae60', color: 'white', marginRight: 5 }}
                  >
                    Approve
                  </button>
                  <button 
                    onClick={() => handleRejectPending(user._id)}
                    style={{ backgroundColor: '#e74c3c', color: 'white' }}
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Approved Sailing Members</h2>
      {removeMessage && (
        <div style={{ 
          color: removeMessageType === 'success' ? '#28a745' : '#dc3545', 
          marginBottom: 8 
        }}>
          {removeMessage}
        </div>
      )}
      <table className="admin-members-table">
        <thead>
          <tr>
            <th>Avatar</th>
            <th>Membership Number</th>
            <th>Name</th>
            <th>Email</th>
            <th>Roles</th>
          </tr>
        </thead>
        <tbody>
          {members.map(m => (
            <tr 
              key={m._id} 
              className="clickable-row" 
              onClick={() => handleUserClick(m)}
              title="Click to view user details"
            >
              <td>
                <div className="avatar-cell">
                  <Avatar user={{ ...m, id: m._id }} size={32} />
                </div>
              </td>
              <td>{m.sailingNumber}</td>
              <td>{m.name}</td>
              <td>{m.email}</td>
              <td>{m.role || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>



      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="user-details-modal-overlay">
          <div className="user-details-modal">
            <div className="user-details-header">
              <h2>{selectedUser.name} - User Details</h2>
              <button className="close-modal-btn" onClick={closeUserDetails}>×</button>
            </div>
            <div className="user-details-content">
              {bookingHistoryLoading && !bookingHistoryLoaded ? (
                <div>Loading booking history...</div>
              ) : bookingHistoryError ? (
                <div style={{ color: 'red' }}>{bookingHistoryError}</div>
              ) : (() => {
                const stats = calculateUserStats(selectedUser._id, fullBookings || allBookings);
                return (
                  <>
                    {/* Basic User Info */}
                    <div className="user-info-section">
                      <h3>User Information</h3>
                      <div className="user-info-grid">
                        <div className="info-item">
                          <label>Name:</label>
                          <div className="info-value">
                            {editField === 'name' ? (
                              <>
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  required
                                  className="edit-input"
                                />
                                <div className="button-group">
                                  <button onClick={saveEdit} className="save-btn">Save</button>
                                  <button type="button" className="cancel-btn" onClick={() => setEditField('')}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span>{selectedUser.name}</span>
                                <div className="button-group">
                                  <button onClick={() => startEdit('name')} className="edit-btn">Edit</button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="info-item">
                          <label>Email:</label>
                          <div className="info-value">
                            {editField === 'email' ? (
                              <>
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  required
                                  className="edit-input"
                                />
                                <div className="button-group">
                                  <button onClick={saveEdit} className="save-btn">Save</button>
                                  <button type="button" className="cancel-btn" onClick={() => setEditField('')}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span>{selectedUser.email}</span>
                                <div className="button-group">
                                  <button onClick={() => startEdit('email')} className="edit-btn">Edit</button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="info-item">
                          <label>Membership No.:</label>
                          <div className="info-value">
                            {editField === 'sailingNumber' ? (
                              <>
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  required
                                  className="edit-input"
                                />
                                <div className="button-group">
                                  <button onClick={saveEdit} className="save-btn">Save</button>
                                  <button type="button" className="cancel-btn" onClick={() => setEditField('')}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span>{selectedUser.sailingNumber}</span>
                                <div className="button-group">
                                  <button onClick={() => startEdit('sailingNumber')} className="edit-btn">Edit</button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="info-item role-item">
                          <label className="role-label">Role:</label>
                          <div className="info-value">
                            {editField === 'role' ? (
                              <>
                                <select
                                  value={editValue}
                                  onChange={e => setEditValue(e.target.value)}
                                  required
                                  className="edit-select"
                                >
                                  <option value="">Select Role</option>
                                  <option value="user">User</option>
                                  <option value="admin">Admin</option>
                                  <option value="pending">Pending</option>
                                </select>
                                <div className="button-group">
                                  <button onClick={saveEdit} className="save-btn">Save</button>
                                  <button type="button" className="cancel-btn" onClick={() => setEditField('')}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span>{selectedUser.role || 'User'}</span>
                                <div className="button-group">
                                  <button onClick={() => startEdit('role')} className="edit-btn">Edit</button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="info-item avatar-item">
                          <label className="avatar-label">Avatar:</label>
                          <div className="info-value">
                            {editField === 'avatar' ? (
                              <>
                                <span>Are you sure you want to remove the avatar?</span>
                                <div className="button-group">
                                  <button onClick={saveEdit} className="save-btn">Yes</button>
                                  <button type="button" className="cancel-btn" onClick={() => setEditField('')}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <span>{selectedUser.avatar ? 'Set' : 'None'}</span>
                                {selectedUser.avatar && (
                                  <div className="button-group">
                                    <button onClick={() => startEdit('avatar')} className="edit-btn">Remove Avatar</button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {editMessage && <div className="edit-message">{editMessage}</div>}
                      {/* Remove user button */}
                      <div className="remove-user-section">
                        <button className="remove-user-btn" onClick={removeUser}>Remove User</button>
                        {modalRemoveMessage && (
                          <div className={`remove-user-message ${modalRemoveMessageType === 'success' ? 'success' : 'error'}`}>
                            {modalRemoveMessage}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="stats-section">
                      <h3>Booking Statistics</h3>
                      <div className="stats-grid">
                        <div className="stat-card">
                          <div className="stat-number">{stats.totalBookings}</div>
                          <div className="stat-label">Total Bookings</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-number">{stats.totalHours}</div>
                          <div className="stat-label">Total Hours</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-number">{stats.assetUsage.length}</div>
                          <div className="stat-label">Assets Used</div>
                        </div>
                      </div>
                    </div>

                    {/* Past Asset Usage */}
                    {stats.assetUsage.length > 0 && (
                      <div className="asset-usage-section">
                        <h3>Past Asset Usage (by Hours)</h3>
                        <div className="asset-usage-list">
                          {stats.assetUsage.map((asset, index) => (
                            <div key={asset.assetId} className="asset-usage-item">
                              <div className="asset-name">{asset.assetName}</div>
                              <div className="asset-stats">
                                <span className="usage-hours">{asset.totalHours}h total</span>
                                <span className="usage-count">{asset.bookingCount} booking{asset.bookingCount !== 1 ? 's' : ''}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Past Bookings */}
                    <div className="past-bookings-section">
                      <h3>Past Bookings</h3>
                      {stats.pastBookings.length > 0 ? (
                        <div className="bookings-list">
                          {stats.pastBookings.slice(0, 10).map((booking, index) => (
                            <div key={booking.id || index} className="booking-item">
                              <div className="booking-asset">{booking.assetName}</div>
                              <div className="booking-date">
                                {new Date(booking.start).toLocaleDateString()} 
                                <span className="booking-time">
                                  {new Date(booking.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                  {new Date(booking.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                              </div>
                              <div className="booking-duration">
                                {Math.round((new Date(booking.end) - new Date(booking.start)) / (1000 * 60 * 60) * 10) / 10}h
                              </div>
                            </div>
                          ))}
                          {stats.pastBookings.length > 10 && (
                            <div className="more-bookings-note">
                              ... and {stats.pastBookings.length - 10} more past booking{stats.pastBookings.length - 10 !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="no-bookings-message">
                          <p>No past bookings for this user.</p>
                        </div>
                      )}
                    </div>

                    {/* Future Bookings */}
                    {stats.futureBookings.length > 0 && (
                      <div className="future-bookings-section">
                        <h3>Upcoming Bookings</h3>
                        <div className="bookings-list">
                          {stats.futureBookings.slice(0, 5).map((booking, index) => (
                            <div key={booking.id || index} className="booking-item future">
                              <div className="booking-asset">{booking.assetName}</div>
                              <div className="booking-date">
                                {new Date(booking.start).toLocaleDateString()} 
                                <span className="booking-time">
                                  {new Date(booking.start).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                                  {new Date(booking.end).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </span>
                              </div>
                              <div className="booking-duration">
                                {Math.round((new Date(booking.end) - new Date(booking.start)) / (1000 * 60 * 60) * 10) / 10}h
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {stats.totalBookings === 0 && (
                      <div className="no-bookings-message">
                        <h3>📊 No Booking History</h3>
                        <p>This user has not made any bookings yet.</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
